const API_BASE = import.meta.env.VITE_API_BASE || '/api/v1'

export type User = {
  id: number
  name: string
  email: string
  role: string
  balance: number
  balance2: number
  tennhanvat: string | null
}

const TOKEN_KEY = 'amc_token'

export function getToken() {
  return localStorage.getItem(TOKEN_KEY) || sessionStorage.getItem(TOKEN_KEY)
}

export function setToken(token: string, remember = true) {
  clearToken()
  ;(remember ? localStorage : sessionStorage).setItem(TOKEN_KEY, token)
}

export function clearToken() {
  localStorage.removeItem(TOKEN_KEY)
  sessionStorage.removeItem(TOKEN_KEY)
}

export const googleLoginUrl = (next = '/account') =>
  `${API_BASE}/auth/google?next=${encodeURIComponent(next)}`

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = new Headers(init.headers)
  if (init.body && !headers.has('content-type')) {
    headers.set('content-type', 'application/json')
  }
  const token = getToken()
  if (token) headers.set('authorization', `Bearer ${token}`)
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) {
    throw new Error(data.message || `HTTP ${res.status}`)
  }
  return data as T
}

function qs(f: Record<string, string | number | undefined | null>) {
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(f)) if (v !== undefined && v !== null && v !== '') params.set(k, String(v))
  const s = params.toString()
  return s ? `?${s}` : ''
}

function post<T = Record<string, unknown>>(path: string, body: unknown) {
  return request<{ ok: boolean } & T>(path, { method: 'POST', body: JSON.stringify(body) })
}

export const api = {
  home: () => request<{ ok: boolean; data: HomeData }>('/catalog/home'),
  categories: (q?: string) =>
    request<{ ok: boolean; categories: Category[] }>(
      `/catalog/categories${q ? `?${q}` : ''}`,
    ),
  category: (idOrSlug: string) =>
    request<{ ok: boolean; category: CategoryDetail }>(`/catalog/categories/${idOrSlug}`),
  sources: () => request<{ ok: boolean; sources: ProductSource[] }>('/catalog/product-sources'),
  sourceProducts: (id: number, f: { q?: string; category_id?: string; nhom?: string; page?: number } = {}) => {
    const params = new URLSearchParams()
    for (const [k, v] of Object.entries(f)) if (v) params.set(k, String(v))
    const qs = params.toString()
    return request<{
      ok: boolean
      products: Product[]
      categories: { id: number; name: string; count: number }[]
      groups: string[]
      total: number
      total_pages: number
    }>(`/catalog/product-sources/${id}/products${qs ? `?${qs}` : ''}`)
  },
  capes: () => request<{ ok: boolean; capes: Cape[] }>('/catalog/capes'),
  blogs: (limit = 20) =>
    request<{ ok: boolean; total: number; items: Blog[] }>(`/catalog/blogs?limit=${limit}`),
  blog: (uri: string) => request<{ ok: boolean; blog: BlogDetail }>(`/catalog/blogs/${uri}`),
  reviews: (q?: {
    page?: number
    per_page?: number
    category_id?: number
    limit?: number
  }) => {
    const params = new URLSearchParams()
    if (q?.page) params.set('page', String(q.page))
    if (q?.per_page) params.set('per_page', String(q.per_page))
    if (q?.category_id != null) params.set('category_id', String(q.category_id))
    if (q?.limit) params.set('limit', String(q.limit))
    const qs = params.toString()
    return request<{
      ok: boolean
      stats: ReviewStats
      items: Review[]
      page?: number
      per_page?: number
      total_pages?: number
    }>(`/catalog/reviews${qs ? `?${qs}` : ''}`)
  },
  register: (body: { name: string; email: string; password: string; agree_tos: boolean }) =>
    request<{ ok: boolean; token: string; user: User }>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  login: (body: { email: string; password: string }) =>
    request<{ ok: boolean; token: string; user: User }>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  me: () => request<{ ok: boolean; user: User }>('/auth/me'),
  createKeyOrder: (body: {
    category_id: number
    quantity?: number
    coupon?: string
    pay_method?: PayMethod
  }) =>
    request<{ ok: boolean; order: Order }>('/orders/key', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  createCapeOrder: (body: {
    cape_id: number
    character_name: string
    email: string
    pay_method: 'bank' | 'xu'
  }) =>
    request<{ ok: boolean; order: Order | QueueOrder }>('/orders/cape', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  createBuyOrder: (body: {
    product_id: number
    character_name: string
    note?: string
    pay_method?: PayMethod
  }) =>
    request<{ ok: boolean; order: Order | QueueOrder }>('/orders/buyitforme', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  adminKeyIntro: (id: number) =>
    request<{ ok: boolean; intro: KeyIntro }>(`/admin/key-intro/${id}`),
  saveKeyIntro: (id: number, body: { short_desc?: string; description?: string }) =>
    request<{ ok: boolean; intro: KeyIntro }>(`/admin/key-intro/${id}`, {
      method: 'PUT',
      body: JSON.stringify(body),
    }),
  replyTemplates: () =>
    request<{ ok: boolean; templates: string[] }>('/admin/reviews/reply-templates'),
  replyReview: (id: number, reply: string) =>
    request<{ ok: boolean; id: number; admin_reply: string | null }>(
      `/admin/reviews/${id}/reply`,
      { method: 'PUT', body: JSON.stringify({ reply }) },
    ),
  admin: {
    summary: () => request<{ ok: boolean; summary: AdminSummary }>('/admin/summary'),
    reviews: (f: AdminFilter) => request<Paged<AdminReview>>(`/admin/reviews${qs(f)}`),
    decideReview: (id: number, approve: boolean, note: string) =>
      post(`/admin/reviews/${id}/${approve ? 'approve' : 'reject'}`, { note }),
    queue: (f: AdminFilter) => request<Paged<AdminQueue>>(`/admin/queue${qs(f)}`),
    completeQueue: (id: number, note: string) => post(`/admin/queue/${id}/complete`, { note }),
    refundQueue: (id: number, note: string) => post(`/admin/queue/${id}/refund`, { note }),
    lookup: (code: string) =>
      request<{ ok: boolean; transaction: AdminTransaction }>(`/admin/transactions/lookup${qs({ code })}`),
    refundTransaction: (id: number, note: string) => post(`/admin/transactions/${id}/refund`, { note }),
    replaceItems: (id: number, items: string) => post(`/admin/transactions/${id}/replace`, { items }),
    pendingOrders: (f: AdminFilter) => request<Paged<AdminPendingOrder>>(`/admin/orders/pending${qs(f)}`),
    deliverOrder: (id: number, items: string) => post(`/admin/orders/${id}/deliver`, { items }),
    notifyOrder: (id: number, message: string) => post(`/admin/orders/${id}/notify`, { message }),
    refundOrder: (id: number, note: string) => post(`/admin/orders/${id}/refund`, { note }),
    cancelOrder: (id: number, note: string) => post(`/admin/orders/${id}/cancel`, { note }),
    deposits: (f: AdminFilter) => request<Paged<AdminDeposit>>(`/admin/deposits${qs(f)}`),
    approveDeposit: (id: number, received: number, note: string) =>
      post(`/admin/deposits/${id}/approve`, { received, note }),
    denyDeposit: (id: number, note: string) => post(`/admin/deposits/${id}/deny`, { note }),
    cards: (f: AdminFilter) => request<Paged<AdminCard> & { amounts?: number[] }>(`/admin/cards${qs(f)}`),
    creditCard: (id: number, amount: number) => post(`/admin/cards/${id}/credit`, { amount }),
    markCard: (id: number) => post(`/admin/cards/${id}/mark-success`, {}),
    members: (f: AdminFilter) => request<Paged<AdminMember>>(`/admin/members${qs(f)}`),
    member: (id: number) => request<{ ok: boolean; member: AdminMemberDetail }>(`/admin/members/${id}`),
    adjustBalance: (id: number, body: { wallet: 'xu' | 'gem'; op: 'cong' | 'tru'; amount: number; note: string }) =>
      post<{ before: number; after: number }>(`/admin/members/${id}/balance`, body),
  },
  orders: (page = 1, per_page = 10) =>
    request<{
      ok: boolean
      orders: Order[]
      page: number
      total: number
      total_pages: number
    }>(`/orders?page=${page}&per_page=${per_page}`),
  order: (id: number) => request<{ ok: boolean; order: Order }>(`/orders/${id}`),
  payOrderGem: (id: number) =>
    request<{ ok: boolean; order: QueueOrder }>(`/orders/${id}/pay-gem`, { method: 'POST', body: '{}' }),
  reviewOrder: (id: number, body: { stars: number; content: string }) =>
    request<{ ok: boolean }>(`/orders/${id}/review`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  accountSummary: () => request<{ ok: boolean; summary: AccountSummary }>('/account/summary'),
  accountQueue: (page = 1) => request<Paged<QueueRow>>(`/account/queue?page=${page}`),
  balanceChanges: (page = 1) =>
    request<Paged<BalanceChange>>(`/account/balance-changes?page=${page}`),
  myReviews: () => request<{ ok: boolean; items: MyReview[] }>('/account/reviews'),
  deposits: (page = 1) => request<Paged<Deposit>>(`/account/deposits?page=${page}`),
  deposit: (id: number) => request<{ ok: boolean; deposit: Deposit }>(`/account/deposits/${id}`),
  createDeposit: (amount: number) =>
    request<{ ok: boolean; deposit: Deposit }>('/account/deposits', {
      method: 'POST',
      body: JSON.stringify({ amount }),
    }),
  cardOptions: () => request<CardOptions & { ok: boolean }>('/account/cards/options'),
  cards: (page = 1) => request<Paged<CardRow>>(`/account/cards?page=${page}`),
  submitCard: (body: { telco: string; amount: number; serial: string; pin: string }) =>
    request<{ ok: boolean; message: string }>('/account/cards', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  notifications: (page = 1, per_page = 15) =>
    request<Paged<UserNotification> & { unread: number }>(
      `/account/notifications?page=${page}&per_page=${per_page}`,
    ),
  readNotification: (id: string) =>
    request<{ ok: boolean; updated: number }>(`/account/notifications/${encodeURIComponent(id)}/read`, {
      method: 'POST',
    }),
  knowledgeBase: (page = 1, q = '') =>
    request<Paged<ContentBlog>>(
      `/catalog/knowledge-base?page=${page}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
    ),
  resourcePacks: (p: { page?: number; type?: string; version?: string; q?: string }) => {
    const qs = new URLSearchParams()
    if (p.page) qs.set('page', String(p.page))
    if (p.type) qs.set('type', p.type)
    if (p.version) qs.set('version', p.version)
    if (p.q) qs.set('q', p.q)
    return request<Paged<ContentBlog> & { versions: string[] }>(`/catalog/resource-packs?${qs}`)
  },
  servers: (p: { page?: number; q?: string; type?: string }) => {
    const qs = new URLSearchParams()
    if (p.page) qs.set('page', String(p.page))
    if (p.q) qs.set('q', p.q)
    if (p.type) qs.set('type', p.type)
    return request<Paged<GameServer>>(`/catalog/servers?${qs}`)
  },
  addServer: (body: {
    name: string
    ip: string
    web?: string
    description?: string
    modes: string
    type: string
  }) =>
    request<{ ok: boolean; message: string }>('/catalog/servers', {
      method: 'POST',
      body: JSON.stringify(body),
    }),
  downloadProducts: () => request<{ ok: boolean; products: ShopCard[] }>('/catalog/download-products'),
  digitalShop: () =>
    request<{ ok: boolean; industs: Indust[]; products: ShopCard[] }>('/catalog/digital-shop'),
}

export type PayMethod = 'bank' | 'xu' | 'gem'

export type Deposit = {
  id: number
  code: string
  amount: number
  received: number | null
  admin_note: string | null
  status: string
  created_at: string | null
  banks?: {
    id: number
    name: string
    account_no: string
    account_name: string
    icon: string | null
    qr_url: string | null
  }[]
}

export type CardOptions = {
  open: boolean
  telcos: { value: string; label: string }[]
  amounts: number[]
}

export type CardRow = {
  id: number
  serial: string
  pin: string
  telco: string
  amount: number
  status: string
  note: string | null
  created_at: string | null
}

export type UserNotification = {
  id: string
  text: string
  to: string | null
  read: boolean
  created_at: string | null
}

export type ContentBlog = {
  id: number
  title: string
  slug: string
  excerpt: string | null
  created_at: string | null
  is_shader: boolean
  versions: string[]
  image_url: string | null
  helpful?: number
}

export type GameServer = {
  id: number
  name: string
  ip: string
  web: string | null
  description: string | null
  modes: string | null
  type: string
  icon: string | null
  version: string | null
  players: number
  max_players: number
  motd: string | null
}

export type ShopCard = {
  id: number
  name: string
  slug: string
  price: number
  price_transfer: number
  deliver: string
  indust_id?: number | null
  short_desc: string | null
  image_url: string | null
}

export type Indust = {
  id: number
  name: string
  description: string | null
  image_url: string | null
}

export type KeyIntro = {
  id: number
  name: string
  slug: string
  short_desc: string
  description: string
}

export type Category = {
  id: number
  name: string
  slug: string
  price: number
  price_transfer: number
  tags: string[]
  deliver: string
  status: string
  group_id?: number | null
  short_desc?: string | null
}

export type CategoryDetail = Category & {
  stock: number | null
  description: string | null
}

export type ProductSource = {
  id: number
  name: string
  description: string | null
  image_url: string | null
  product_count: number
}

export type Product = {
  id: number
  name: string
  price: number
  price_transfer: number
  gem_price: number | null
  old_price: number | null
  source_id: number
  category_id: number | null
  group: string | null
  description: string | null
  required_note: boolean
  required_text: string | null
  image: string | null
  media: 'image' | 'video' | null
}

export type Cape = {
  id: number
  name: string
  category: string | null
  price: number
  price_transfer: number
  old_price?: number | null
  featured: boolean
  image_url?: string | null
}

export type Blog = {
  id: number
  title: string
  slug: string
  excerpt: string | null
  created_at: string
  is_shader: boolean
  image_url?: string | null
}

export type PackDetail = {
  cover_url: string | null
  gallery: string[]
  versions: string[]
  downloads: { url: string; label: string }[]
}

export type BlogDetail = Blog & { content: string; pack?: PackDetail }

export type ReviewStats = { count: number; avg: number }
export type Review = {
  id: number
  stars: number
  content: string
  created_at: string
  user_name: string
  admin_reply?: string | null
  category_id?: number | null
  product_id?: number | null
}

export type Order = {
  id: number
  trans_id: string
  type: string
  status: string
  category_id: number | null
  product_id: number | null
  unit_price: number
  quantity: number
  discount: number
  total: number
  verify_code: string
  created_at: string
  payment?: {
    bank_name: string
    account_no: string
    account_name: string
    amount: number
    content: string
    vietqr_url: string
  }
  name?: string | null
  character_name?: string | null
  items?: DeliveredItem[]
  replace_items?: DeliveredItem[]
  keys?: string[]
  pay_method?: 'bank' | 'xu' | 'gem' | null
  admin_note?: string | null
  deliver_note?: string | null
  is_minecraft?: boolean
  videos?: string[]
  guide_blog?: { uri: string; title: string } | null
  reviewed?: boolean
  can_review?: boolean
}

export type DeliveredItem = {
  id: number
  key?: string
  item?: string
  item_preview: string
  trangthai: string
}

export type Paged<T> = {
  ok: boolean
  items: T[]
  page: number
  per_page: number
  total: number
  total_pages: number
}

export type AccountSummary = {
  balance: number
  balance2: number
  orders_done: number
  deposited: number
  tier: string
}

export type QueueRow = {
  id: number
  code: string
  character_name: string | null
  product_name: string
  note: string | null
  admin_note: string | null
  total: number
  created_at: string | null
  status: string
}

export type BalanceChange = {
  id: number
  before: number
  after: number
  amount: number
  kind: string
  wallet: string | null
  note: string | null
  created_at: string | null
}

export type MyReview = {
  id: number
  stars: number
  content: string
  created_at: string | null
  status: string
  admin_reply: string | null
  product_name: string | null
  image_url: string | null
}

export type QueueOrder = {
  kind: 'queue'
  id: number
  status: string
  product_id?: number
  character_name?: string
  total: number
  pay_method: string
}

export type HomeData = {
  bank: { name: string; account: string; holder: string }
  key_product: (CategoryDetail & { image_url?: string | null }) | null
  fresh_license: CategoryDetail | null
  minecoins: Category[]
  featured_categories: Category[]
  buyitforme_sources: ProductSource[]
  reviews: { stats: ReviewStats; items: Review[] }
  blogs: Blog[]
  stats?: {
    reviews: number
    transactions: number
    categories: number
    items_delivered: number
    users: number
  }
  recent_purchases?: {
    id: number
    user_name: string
    product_name: string
    amount: number
    at: string
  }[]
}

export type AdminFilter = Record<string, string | number | undefined>

export type AdminSummary = {
  reviews: number
  queue: number
  pending_orders: number
  deposits: number
  cards: number
}

export type AdminUser = { id: number; name: string | null; email: string | null }

export type AdminReview = {
  id: number
  user: AdminUser
  stars: number
  content: string
  admin_note: string | null
  product_name: string | null
  order_id: number | null
  status: string
  created_at: string | null
}

export type AdminQueue = {
  id: number
  trans_id: string | null
  transaction_id: number | null
  user: AdminUser
  product_name: string
  group: string | null
  source: { name: string | null; link: string | null }
  image: string | null
  media: 'image' | 'video' | null
  cape_skin: string | null
  character_name: string | null
  note: string | null
  admin_note: string | null
  total: number
  pay: 'xu' | 'gem' | 'bank'
  status: string
  created_at: string | null
}

export type AdminItem = { id: number; text: string; at: string | null }

export type AdminTransaction = {
  id: number
  trans_id: string
  verify_code: string | null
  type: string
  user: AdminUser
  product_name: string | null
  unit_price: number
  quantity: number
  discount: number
  total: number
  pay: 'xu' | 'gem' | 'bank' | null
  status: string
  user_note: string | null
  admin_note: string | null
  deliver_note: string | null
  created_at: string | null
  items: AdminItem[]
  replace_items: AdminItem[]
  queue: { id: number; status: string; character_name: string | null; admin_note: string | null } | null
  can_refund: boolean
  can_replace: boolean
}

export type AdminPendingOrder = {
  id: number
  trans_id: string
  user: AdminUser
  product_name: string | null
  user_note: string | null
  quantity: number
  total: number
  pay: 'xu' | 'gem' | 'bank' | null
  auto: boolean
  created_at: string | null
}

export type AdminDeposit = {
  id: number
  user: AdminUser
  code: string
  amount: number
  received: number | null
  admin_note: string | null
  status: string
  created_at: string | null
}

export type AdminCard = {
  id: number
  user: AdminUser
  telco: string
  serial: string
  pin: string
  trans_id: string | null
  amount: number
  note: string | null
  status: string
  created_at: string | null
}

export type AdminMember = {
  id: number
  name: string
  email: string
  role: string
  status: string
  balance: number
  balance2: number
  created_at: string | null
}

export type AdminMemberDetail = AdminMember & { changes: BalanceChange[] }

export function formatVnd(n: number) {
  return n.toLocaleString('vi-VN') + ' đ'
}
